import cx from 'classnames';
import { ButtonV2 } from 'components/ui';
import { XClose } from 'ethena-icons';
import { AnimatePresence, motion } from 'framer-motion';
import { EthenaDate } from 'lib/helpers/dateHelper';
import { useRouter } from 'next/router';
import {
  ElementType,
  HTMLAttributes,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import store2 from 'store2';
import css from './Alert.module.scss';

export interface IAlertProps extends HTMLAttributes<HTMLElement> {
  color:
    | 'blue'
    | 'dark-gray'
    | 'green'
    | 'light-gray'
    | 'red'
    | 'theme'
    | 'yellow'
    | 'white';
  className?: string;
  icon?: ElementType;
  radius?: number;
}

interface IDismissibleAlertProps extends IAlertProps {
  name: string;
}

export const DismissibleAlert = ({
  className,
  children,
  name,
  ...rest
}: IDismissibleAlertProps) => {
  const { asPath } = useRouter();

  // Adding path prevent collisions.
  const store = useMemo(() => store2.namespace(name + '::' + asPath), [name]);

  const [isVisible, setIsVisible] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (!store.has('lastDismissedAt')) {
      setIsVisible(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    store.set('lastDismissedAt', new EthenaDate().toISOString());
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <Alert className={cx(css.dismissible, className)} {...rest}>
          <div>{children}</div>
          <ButtonV2
            className={css.closeButton}
            onClick={handleDismiss}
            variant="link"
          >
            <XClose width={20} height={20} />
          </ButtonV2>
        </Alert>
      )}
    </AnimatePresence>
  );
};

export const Alert = ({
  children,
  className,
  color,
  icon: Icon,
  radius = 8,
}: IAlertProps) => (
  <motion.div
    animate={{ opacity: 1 }}
    aria-live="polite"
    className={cx(css.alert, css[color], className, {
      [css.withIcon]: !!Icon,
    })}
    exit={{ opacity: 0 }}
    initial={{ opacity: 0 }}
    style={{ borderRadius: radius }}
    transition={{ duration: 0.1 }}
  >
    {!!Icon && <Icon className={css.icon} height={14} width={14} />}
    {children}
  </motion.div>
);
